import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import {
  TrendingUp,
  Analytics,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Assessment,
  BarChart as StatisticsIcon,
} from '@mui/icons-material';
import StatCard from '../../components/StatCard';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { ROLES, SchoolActivity } from '@shared/types';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

interface CategoryScore {
  category: string;
  type: string;
  average_score: number;
}

const StatisticsPage = observer(() => {
  const { t, i18n } = useTranslation();
  const adminStore = useStoreByName('adminStore');
  const userStore = useStoreByName('userStore');

  const { scoreStatistics, userGrowthData, isLoading } = adminStore;
  const isRtl = i18n.language === 'he';

  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;

  useEffect(() => {
    adminStore.loadScoreStatistics();
    adminStore.loadUserGrowthData();
  }, [adminStore, adminStore.selectedSchoolId]);

  const prepareReadingTypeData = () => {
    if (!scoreStatistics?.totalScores) return [];
    return toJS([
      {
        name: t('regularReading'),
        value: scoreStatistics.totalScores.reading.count,
      },
      {
        name: t('byHeartReading'),
        value: scoreStatistics.totalScores.byHeart.count,
      },
    ]);
  };

  const prepareCategoryData = () => {
    if (!scoreStatistics?.categoryAverages) return [];

    const categories: {
      [key: string]: { category: string; [type: string]: string | number };
    } = {};

    toJS(scoreStatistics.categoryAverages).forEach((item: CategoryScore) => {
      if (!categories[item.category]) {
        categories[item.category] = {
          category: item.category,
        };
      }
      categories[item.category][item.type] = item.average_score;
    });

    return toJS(Object.values(categories));
  };


  return (
    <div className="statistics-container" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="head-line">
        <h1 className="title">
          {isPrincipal ? t('schoolStatistics') : t('statistics')} <StatisticsIcon />
        </h1>
        <p className="subtitle">
          {adminStore.selectedSchoolId ? (
            <>
              {t('schoolPerformanceDataFor')}{' '}
              <span style={{ fontWeight: 'bold', color: '#667eea' }}>
                {adminStore.availableSchools.find((school: SchoolActivity) => school.id == adminStore.selectedSchoolId)
                  ?.name || t('selectedSchool')}
              </span>
            </>
          ) : (
            <>
              {t('schoolPerformanceDataAll')}{' '}
              <span style={{ fontWeight: 'bold', color: '#4caf50' }}>{t('allSchools')}</span>
            </>
          )}
        </p>
      </div>
      {isLoading ? (
        <div className="loadingContainer">
          <CircularProgress size={60} style={{ color: '#667eea' }} />
        </div>
      ) : (
        <>
          <div className="statisticsSection">
            <div className="statisticsGrid">
              <StatCard
                title={t('regularReading')}
                value={scoreStatistics?.totalScores?.reading?.count || 0}
                icon={<Assessment />}
                variant="success"
                isLoading={isLoading}
              />
              <StatCard
                title={t('byHeartReading')}
                value={scoreStatistics?.totalScores?.byHeart?.count || 0}
                icon={<BarChartIcon />}
                variant="purple"
                isLoading={isLoading}
              />
            </div>
          </div>

          <div className="chartsSection">
            <h2 className="sectionTitle">{t('performanceCharts')}</h2>
            <div className="chartsGrid">
              <div className="chartCard">
                <div className="chartCardContent">
                  <div className="chartHeader">
                    <TrendingUp style={{ color: '#667eea', marginInlineEnd: '8px' }} />
                    <h3 className="chartTitle">{t('userGrowth')}</h3>
                  </div>
                  {userGrowthData && userGrowthData.length > 0 ? (
                    <div className="chartContainer">
                      <ResponsiveContainer>
                        <LineChart data={toJS(userGrowthData)?.slice(-6) || []}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="date" fontSize={10} axisLine={false} tickLine={false} />
                          <YAxis fontSize={10} axisLine={false} tickLine={false} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="newUsers"
                            stroke="#667eea"
                            strokeWidth={3}
                            dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="totalUsers"
                            stroke="#f093fb"
                            strokeWidth={3}
                            dot={{ fill: '#f093fb', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#f093fb', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="noDataContainer">
                      <span>{t('noData')}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="chartCard">
                <div className="chartCardContent">
                  <div className="chartHeader">
                    <PieChartIcon style={{ color: '#f093fb', marginInlineEnd: '8px' }} />
                    <h3 className="chartTitle">{t('readingTypeDistribution')}</h3>
                  </div>
                  {prepareReadingTypeData().length > 0 ? (
                    <div className="pieChartContainer">
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={prepareReadingTypeData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={62}
                            outerRadius={96}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${((Number(percent) || 0) * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {prepareReadingTypeData().map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="noDataContainer">
                      <span>{t('noData')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="categorySection">
            <div className="categoryGrid">
              <div className="chartCard">
                <div className="chartCardContent">
                  <div className="chartHeader">
                    <Analytics style={{ color: '#4facfe', marginInlineEnd: '8px' }} />
                    <h3 className="chartTitle">{t('averageScoresByCategory')}</h3>
                  </div>
                  {prepareCategoryData().length > 0 ? (
                    <div className="chartContainer">
                      <ResponsiveContainer>
                        <BarChart data={prepareCategoryData()}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                          <XAxis dataKey="category" fontSize={10} axisLine={false} tickLine={false} />
                          <YAxis fontSize={10} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(value, name) => [
                              `${Number(value).toFixed(2)}%`,
                              name === 'reading' ? t('regularReading') : t('byHeartReading'),
                            ]}
                            contentStyle={{
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            }}
                          />
                          <Bar dataKey="reading" fill="#667eea" name="reading" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="byHeart" fill="#f093fb" name="byHeart" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="noDataContainer">
                      <span>{t('noData')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default StatisticsPage;
